import { z } from 'zod';
import { UserDocumentSourceType } from '../enums/userDocument';
import { STORED_ASSET_NAME_MAX_LENGTH, STORED_ASSET_NAME_RE } from './tenant';

export const CreateUserDocumentDto = z
  .object({
    userId: z.string().uuid(),
    organizationId: z.string().uuid().optional(),
    sourceType: z.nativeEnum(UserDocumentSourceType),
    link: z.string().min(1),
    title: z.string().min(1).max(255),
    categoryId: z.string().uuid(),
    categoryTagId: z.string().uuid().optional(),
    isVisibleToClient: z.boolean().default(true),
    isVisibleToTrainer: z.boolean().default(true),
    dateTime: z.string().datetime(),
  })
  // A FILE link is a stored ASSET NAME, never a path: the server appends it
  // to `<tenant>/<user>/documents/`, so a multi-segment value addresses a key
  // outside that prefix and only ever dies later, at the upload proof's
  // storage round trip, with the wrong error. Same rule and message as every
  // sibling upload DTO (`UploadedAssetName`). An EXTERNAL_LINK stores a URL,
  // not a key in our bucket — deliberately untouched.
  .superRefine((dto, ctx) => {
    if (dto.sourceType !== UserDocumentSourceType.FILE) return;
    if (
      dto.link.length > STORED_ASSET_NAME_MAX_LENGTH ||
      !STORED_ASSET_NAME_RE.test(dto.link)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'VALIDATION__InvalidAssetName',
        path: ['link'],
      });
    }
  });

export const UpdateUserDocumentDto = z.object({
  title: z.string().min(1).max(255).optional(),
  categoryId: z.string().uuid().optional(),
  categoryTagId: z.string().uuid().nullable().optional(),
});

export const ListUserDocumentsQueryDto = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid().optional(),
});

export const GetDocumentUploadUrlQueryDto = z.object({
  mediaType: z.enum([
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ]),
  organizationId: z.string().uuid(),
  userId: z.string().uuid(),
});

export type ApiCreateUserDocumentDto = z.infer<typeof CreateUserDocumentDto>;
export type ApiUpdateUserDocumentDto = z.infer<typeof UpdateUserDocumentDto>;
export type ApiListUserDocumentsQueryDto = z.infer<
  typeof ListUserDocumentsQueryDto
>;
export type ApiGetDocumentUploadUrlQueryDto = z.infer<
  typeof GetDocumentUploadUrlQueryDto
>;
